/**
 * StaticDataService — Reads author JSONs bundled under src/data/authors/*.json.
 *
 * Loading strategy:
 *   - Vite's `import.meta.glob` with `{ eager: true, import: 'default' }`
 *     pulls every JSON into the build at compile time.
 *   - authors-index.json is the authoritative ordering / inclusion list:
 *     only ids listed there are exposed (allows staging unfinished JSONs in
 *     the folder without breaking the catalog).
 *
 * At init() time we also:
 *   - Sort authors chronologically (birth_year asc; undated authors are placed
 *     by epoch via chronoYear — Beowulf, undated but ancient, goes first).
 *   - Populate Passage.work_id on every nested passage (callers can treat
 *     passages as flat, knowing their parent work).
 *   - Build per-author lookup maps (works by id, passages by id).
 */

import type {
  Author,
  Work,
  Passage,
  ThemeTreatment,
  MajorWork,
} from '@/data/schema';
import type { IDataService, AuthorFilters } from './IDataService';
import { chronoYear } from '@/data/chronology';

interface AuthorIndex {
  works: Map<string, Work>;
  passages: Map<string, Passage>;
}

// Vite glob: every author JSON gets bundled. The key looks like
// '/src/data/authors/john-keats.json'.
const AUTHOR_MODULES = import.meta.glob<Author>(
  '/src/data/authors/*.json',
  { eager: true, import: 'default' },
);

// authors-index.json is the source of truth for which ids ship.
import authorsIndex from '@/data/authors-index.json';

export class StaticDataService implements IDataService {
  private authors: Author[] = [];
  private byId = new Map<string, Author>();
  private indexes = new Map<string, AuthorIndex>();
  private initialized = false;

  async init(): Promise<void> {
    if (this.initialized) return;

    const ids = authorsIndex as string[];
    const loaded: Author[] = [];

    for (const id of ids) {
      const path = `/src/data/authors/${id}.json`;
      const mod = AUTHOR_MODULES[path];
      if (!mod) {
        console.warn(`[data] authors-index references "${id}" but no JSON found at ${path}`);
        continue;
      }
      const author = mod;
      loaded.push(author);
      this.buildIndex(author);
    }

    // Chronological: by birth_year ascending; undated authors placed by epoch
    // (so Beowulf — undated but ancient — sorts first, not last).
    loaded.sort((a, b) => chronoYear(a).year - chronoYear(b).year);

    this.authors = loaded;
    for (const a of loaded) this.byId.set(a.id, a);

    this.initialized = true;
    console.info(`[data] StaticDataService ready — ${this.authors.length} authors loaded`);
  }

  private buildIndex(author: Author): void {
    const works = new Map<string, Work>();
    const passages = new Map<string, Passage>();
    for (const w of author.works) {
      works.set(w.id, w);
      if (isMajor(w) && w.passages) {
        for (const p of w.passages) {
          // Populate parent reference so callers can treat passages as flat.
          if (!p.work_id) p.work_id = w.id;
          passages.set(p.id, p);
        }
      }
    }
    this.indexes.set(author.id, { works, passages });
  }

  listAuthors(filters?: AuthorFilters): Author[] {
    this.assertReady();
    if (!filters) return [...this.authors];

    const needle = filters.search?.trim().toLowerCase();
    return this.authors.filter(a => {
      if (filters.periods?.length && !filters.periods.some(p => a.periods.includes(p))) return false;
      if (filters.movements?.length && !filters.movements.some(m => a.movements.includes(m))) return false;
      if (filters.genres?.length && !filters.genres.some(g => a.genres.includes(g))) return false;
      if (needle) {
        const hay = `${a.name} ${a.tagline}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }

  getAuthor(id: string): Author | null {
    this.assertReady();
    return this.byId.get(id) ?? null;
  }

  getWork(authorId: string, workId: string): Work | null {
    this.assertReady();
    return this.indexes.get(authorId)?.works.get(workId) ?? null;
  }

  getPassage(authorId: string, passageId: string): Passage | null {
    this.assertReady();
    return this.indexes.get(authorId)?.passages.get(passageId) ?? null;
  }

  listPassages(authorId: string): Passage[] {
    this.assertReady();
    const idx = this.indexes.get(authorId);
    return idx ? [...idx.passages.values()] : [];
  }

  listThemeTreatments(authorId: string): ThemeTreatment[] {
    this.assertReady();
    return this.byId.get(authorId)?.theme_treatments ?? [];
  }

  listAuthorsByTheme(themeId: string): Author[] {
    this.assertReady();
    return this.authors.filter(a =>
      a.theme_treatments.some(t => t.theme_id === themeId),
    );
  }

  private assertReady(): void {
    if (!this.initialized) {
      throw new Error('[data] StaticDataService used before init()');
    }
  }
}

function isMajor(w: Work): w is MajorWork {
  return w.is_major === true;
}
